import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  Chip,
  Stack,
  LinearProgress,
  useMediaQuery,
  Fade,
  Popper,
  TextField,
  Divider,
  CardContent,
  Card,
  CircularProgress,
  Button,
  IconButton,
  Link,
  Grid,
  Snackbar,
  Alert,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import TrendingFlatRoundedIcon from '@mui/icons-material/TrendingFlatRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import ROIForm from '../components/Roi/RoiForm';
import Logo from '../components/Logo';
import SettingsIcon from '@mui/icons-material/Settings';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import guaranteed from '../Image/guaranteed.png';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { roi } from '../services/CustomerService';
import AppointmentRequestPopper from '../components/Roi/AppointmentRequestPopper';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PartnerProgram from '../components/Roi/PartnerProgram';
import Footer from '../components/Footer';
import FAQSection from '../components/Roi/FAQSection';
import BillChart from '../components/Roi/BillChart';

function RoiPage() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const initialFormData = {
    name: queryParams.get('name') || '',
    property_type: queryParams.get('property-type') || '',
    state: queryParams.get('state') || '',
    max_bill: queryParams.get('maxbill') || '',
    min_bill: queryParams.get('minbill') || '',
    load: queryParams.get('load') || '',
    phone: queryParams.get('number') || '',
    pin_code: queryParams.get('pincode') || '',
    custom_plant_size: queryParams.get('customPlantSize') || '',
    consent: queryParams.get('consent') === 'true',
  };

  const theme = useTheme();
  const [pdfUrl, setPdfUrl] = useState(null);

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const [selectedChip, setSelectedChip] = useState(0);
  const [roiData, setRoiData] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [viewROIForm, setViewROIForm] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState();
  const [customPlantSizeValue, setCustomPlantSizeValue] = useState('');
  const [prjectValue, setProjectValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookAnchorEl, setBookAnchorEl] = useState(null);
  const [bookPopperOpen, setBookPopperOpen] = useState(false);
  const [reportFormData, setReportFormData] = useState({
    name: initialFormData.name || '',
    phone: initialFormData.phone || '',
    pin_code: initialFormData.pin_code || '',
    email: '',
  });
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [selectedEstimateType, setSelectedEstimateType] = useState('yearly');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleClick = (newPlacement) => (event) => {
    if (!formData) {
      alert('Please fill and submit the ROI form first before editing values.');
      return;
    }

    setAnchorEl(event.currentTarget);
    setOpen((prev) => placement !== newPlacement || !prev);
    setPlacement(newPlacement);

    setCustomPlantSizeValue(formData.custom_plant_size || '');
    setProjectValue(formData.project_value || '');
  };

  const handleGetMyReportClick = (event) => {
    setBookAnchorEl(event.currentTarget);
    setBookPopperOpen(true);
  };

  const handleBookPopperClose = () => {
    setBookPopperOpen(false);
    setPdfUrl(null);
  };

  const handleAppointmentFormChange = (event) => {
    setReportFormData({
      ...reportFormData,
      [event.target.name]: event.target.value,
    });
  };

  const handleAppointmentSubmit = async (send_on_whatsapp = 0) => {
    if (
      !reportFormData.name ||
      !reportFormData.phone ||
      !reportFormData.pin_code
    ) {
      setSnackbar({
        open: true,
        message:
          'Please fill in all required fields (name, phone number, and pin code)',
        severity: 'error',
      });
      return;
    }

    setReportSubmitting(true);

    try {
      const completePayload = {
        property_type: formData?.property_type || '',
        state: formData?.state || '',
        max_bill: formData?.max_bill || '',
        min_bill: formData?.min_bill || '',
        load: formData?.load || '',
        custom_plant_size: formData?.custom_plant_size || '',
        pdf_flag: '1',
        send_on_whatsapp: send_on_whatsapp,

        customer_name: reportFormData.name,
        customer_phone: reportFormData.phone,
        pin_code: reportFormData.pin_code,
        email: reportFormData.email,
      };

      const response = await roi(completePayload);

      setPdfUrl(response?.access_url?.url);
      setSnackbar({
        open: true,
        message:
          'Your solar savings report has been generated and sent successfully!',
        severity: 'success',
      });

      // setReportFormData({
      //   name: '',
      //   phone: '',
      //   pin_code: '',
      //   email: '',
      // });
    } catch (error) {
      console.error('Error generating report:', error);
      setSnackbar({
        open: true,
        message:
          'Failed to generate your solar savings report. Please try again.',
        severity: 'error',
      });
    } finally {
      setReportSubmitting(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const submitUpdatedRoiData = async (updatedFormData) => {
    if (isSubmitting) return;

    try {
      if (!formData) {
        throw new Error(
          'No initial form data found. Please fill and submit the ROI form first.',
        );
      }

      const completePayload = {
        property_type: formData.property_type || 'Residential',
        state: formData.state || '',
        max_bill: formData.max_bill || '',
        min_bill: formData.min_bill || '',
        load: formData.load || '',
        custom_plant_size:
          updatedFormData.custom_plant_size ||
          formData.custom_plant_size ||
          formData.load ||
          '3',
        pdf_flag: '0',
        project_value: updatedFormData.project_value || '',
      };

      const missingFields = [];
      ['property_type', 'state', 'max_bill', 'min_bill', 'load'].forEach(
        (field) => {
          if (!completePayload[field]) {
            missingFields.push(field);
          }
        },
      );

      if (missingFields.length > 0) {
        throw new Error(
          `Missing required fields: ${missingFields.join(', ')}. Please complete the ROI form first.`,
        );
      }

      const response = await roi(completePayload);

      setRoiData(response);
      setFormData({
        ...formData,
        ...updatedFormData,
        custom_plant_size: completePayload.custom_plant_size,
      });
      setViewROIForm(false);
    } catch (error) {
      console.error('Error details:', error);
      alert(
        `${error.message || 'Failed to update ROI data. Please try again.'}`,
      );

      if (error.message && error.message.includes('Missing required fields')) {
        setViewROIForm(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  const mobileHeaderStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    py: 3,
  };

  const defaultHeaderStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    py: 3,
  };

  const handleChipClick = (index) => {
    setSelectedChip(index);
  };

  const handleRoiSubmit = (data) => {
    setRoiData(data);
    setViewROIForm(false);
    setViewROIForm(false);
  };

  const handlFromData = (data) => {
    setFormData(data);
  };

  const dataToShow = [
    {
      title: 'Annual',
      icon: <CalendarMonthRoundedIcon fontSize='small' />,
    },
    {
      title: 'High bill month',
      icon: <TrendingUpRoundedIcon fontSize='small' />,
    },
    {
      title: 'Moderate billl month',
      icon: <TrendingFlatRoundedIcon fontSize='small' />,
    },
    {
      title: 'Low bill month',
      icon: <TrendingDownRoundedIcon fontSize='small' />,
    },
  ];

  const VisualData = ({ data, roi_Bylease, roi_ByOwn }) => {
    const safeNumber = (value) => value ?? 0;

    const tableData = [
      {
        particular: (
          <Typography variant='body1' fontWeight={'bold'}>
            Estimated savings
          </Typography>
        ),
        doNothing: (
          <Typography variant='body1' fontWeight={'bold'}>
            {Math.floor(
              safeNumber(
                1 -
                  data?.do_nothing.total_energy_expense /
                    data?.do_nothing.total_energy_expense,
              ) * 100,
            )}
            %
          </Typography>
        ),
        getLease: (
          <Typography variant='body1' fontWeight={'bold'}>
            {Math.floor(
              safeNumber(
                1 -
                  data?.lease.total_energy_expense /
                    data?.do_nothing.total_energy_expense,
              ) * 100,
            )}
            %
          </Typography>
        ),
        ownPlant: (
          <Typography variant='body1' fontWeight={'bold'}>
            {Math.floor(
              (safeNumber(data?.own.total_energy_expense) /
                data?.do_nothing.total_energy_expense) *
                100,
            ) >= 0
              ? Math.floor(
                  safeNumber(
                    1 -
                      data?.own.total_energy_expense /
                        data?.do_nothing.total_energy_expense,
                  ) * 100,
                )
              : 100}
            %
          </Typography>
        ),
      },
      {
        particular: 'Add: Fixed charges',
        doNothing: safeNumber(data?.do_nothing?.fixed),
        getLease: safeNumber(data?.lease?.fixed),
        ownPlant: safeNumber(data?.own?.fixed),
      },
      {
        particular: 'Add: Variable charges',
        doNothing: safeNumber(data?.do_nothing?.variable),
        getLease: safeNumber(data?.lease?.variable),
        ownPlant: safeNumber(data?.own?.variable),
      },
      {
        particular: 'Add: Solar charges',
        doNothing: 0,
        getLease: safeNumber(data?.lease?.lease_expense),
        ownPlant: 0,
      },
      {
        particular: 'Add: Taxes & surcharges',
        doNothing: safeNumber(data?.do_nothing?.tax),
        getLease: safeNumber(data?.lease?.tax),
        ownPlant: safeNumber(data?.own?.tax),
      },
      {
        particular: 'Less: Generation incentives',
        doNothing: 0,
        getLease: `- ${safeNumber(data?.lease?.incentives)}`,
        ownPlant: `- ${safeNumber(data?.own?.incentives)}`,
      },
      {
        particular: (
          <Typography variant='body1' fontWeight={'bold'}>
            Energy expense amount
          </Typography>
        ),
        doNothing: (
          <Typography variant='body1' fontWeight={'bold'}>
            {safeNumber(data?.do_nothing.total_energy_expense)}
          </Typography>
        ),
        getLease: (
          <Typography variant='body1' fontWeight={'bold'}>
            {safeNumber(data?.lease.total_energy_expense)}
          </Typography>
        ),
        ownPlant: (
          <Typography variant='body1' fontWeight={'bold'}>
            {safeNumber(data?.own.total_energy_expense)}
          </Typography>
        ),
      },
      {},
      {
        particular: 'Units consumed',
        doNothing: safeNumber(data?.do_nothing?.units),
        getLease: safeNumber(data?.lease?.units),
        ownPlant: safeNumber(data?.own?.units),
      },
      {
        particular: 'Consumption expense per unit',
        doNothing: safeNumber(data?.do_nothing?.unit_rate),
        getLease: safeNumber(data?.lease?.unit_rate),
        ownPlant: safeNumber(data?.own?.unit_rate),
      },
      {
        particular: 'Units produced',
        doNothing: 0,
        getLease: safeNumber(data?.lease?.units_produced),
        ownPlant: safeNumber(data?.own?.units_produced),
      },
      {
        particular: 'Solar plant size',
        doNothing: 0,
        getLease: safeNumber(data?.lease?.plant_size),
        ownPlant: safeNumber(data?.own?.plant_size),
      },
    ];

    const chartData = [
      safeNumber(data?.investment_meter?.solar_lease * 100),
      safeNumber(data?.investment_meter?.solar_ownership * 100),
      safeNumber(data?.investment_meter?.equity * 100),
      safeNumber(data?.investment_meter?.debt * 100),
      safeNumber(data?.investment_meter?.fixed_deposit * 100),
      safeNumber(data?.investment_meter?.savings * 100),
      safeNumber(data?.investment_meter?.do_nothing),
    ];

    const newTableData = {
      1: { value: 10 },
      2: { value: 10 },
      3: { value: 9 },
      4: { value: 9 },
      5: { value: 9 },
      6: { value: 9 },
    };

    const valuesArray = Object.values(newTableData).map((item) => item.value);
    const minVal = Math.min(...valuesArray);
    const maxVal = Math.max(...valuesArray);
    const barColors = valuesArray.map((val) =>
      val === minVal
        ? '#c2c9d6'
        : val === maxVal
          ? theme.palette.secondary.light
          : null,
    );

    const chartLabels = [
      'Postpaid solar',
      'Buy solar',
      'Equity',
      'Debt',
      'FD',
      'Savings',
      'Do nothing',
    ];

    return (
      <Box gap={2} pb={2}>
        {!isMobile && (
          <TableContainer
            component={Paper}
            sx={{ marginBottom: 4 }}
            pt={4}
            style={{ boxShadow: 'none' }}
          >
            <Table size='small' width='100%'>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: '400' }}>Particulars</TableCell>
                  <TableCell sx={{ fontWeight: '400' }} align='right'>
                    Do nothing
                  </TableCell>
                  <TableCell sx={{ fontWeight: '400' }} align='right'>
                    Postpaid solar
                  </TableCell>
                  <TableCell sx={{ fontWeight: '400' }} align='right'>
                    Buy solar
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.map((row) => (
                  <TableRow hover key={row?.particular}>
                    <TableCell fontWeight={'bold'} component='th' scope='row'>
                      {row?.particular}
                    </TableCell>
                    <TableCell align='right'>{row?.doNothing}</TableCell>
                    <TableCell align='right'>{row?.getLease}</TableCell>
                    <TableCell align='right'>{row?.ownPlant}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box mb={2}>
          <Typography variant={isMobile ? 'h5' : 'h4'} pb={2} mb={2} mt={8}>
            Return comparison
          </Typography>
          <BarChart
            sx={{
              '& .MuiChartsAxis-root.MuiChartsAxis-directionY .MuiChartsAxis-line':
                {
                  display: 'none',
                },
              '& .MuiChartsAxis-root.MuiChartsAxis-directionY .MuiChartsAxis-tick':
                {
                  display: 'none',
                },
              '& .MuiChartsAxis-root.MuiChartsAxis-directionY .MuiChartsAxis-tickLabel':
                {
                  fontWeight: 600,
                  fill: 'gray',
                },
              '& .MuiChartsAxis-root.MuiChartsAxis-directionX .MuiChartsAxis-tickLabel':
                {
                  fontWeight: 600,
                },
            }}
            borderRadius={10}
            xAxis={[
              {
                scaleType: 'band',
                data: chartLabels,
                colorMap: {
                  type: 'ordinal',
                  colors: barColors,
                },
              },
            ]}
            yAxis={[
              {
                valueFormatter: (value) => value.toLocaleString() + '%',
              },
            ]}
            series={[
              {
                data: chartData,
                valueFormatter: (value) =>
                  value.toLocaleString('en-IN', {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  }) + '%',
              },
            ]}
            height={300}
          />
        </Box>
      </Box>
    );
  };
  const data = [
    {
      label: 'Bill savings (our guarantee)',
      doNothing: '₹0',
      payLater: `₹${roiData?.annual_bill_profile?.lease?.savings?.toLocaleString('en-IN') || '0'} p. year`,
      buySolar: `₹${roiData?.annual_bill_profile?.own?.savings?.toLocaleString('en-IN') || '0'} p. year`,
    },
    {
      label: 'One time investment',
      doNothing: '₹0',
      payLater: `₹${roiData?.annual_bill_profile?.lease?.investment?.toLocaleString('en-IN') || '0'}\nBuy later option available`,
      buySolar: `₹${roiData?.annual_bill_profile?.own?.investment?.toLocaleString('en-IN') || '0'}`,
    },
    {
      label: 'First year return',
      doNothing: '0',
      payLater: `${roiData?.annual_bill_profile?.lease?.annual_roi?.toLocaleString('en-IN') || '0'}`,
      buySolar: `${roiData?.annual_bill_profile?.own?.annual_roi?.toLocaleString('en-IN') || '0'}`,
    },
  ];
  const getEstimates = () => {
    const profileData =
      {
        yearly: roiData?.annual_bill_profile,
        high: roiData?.max_bill_profile,
        low: roiData?.min_bill_profile,
      }[selectedEstimateType] || roiData?.annual_bill_profile;

    return [
      {
        title: 'With postpaid solar',
        totalCost: `₹${profileData?.lease?.total_energy_expense?.toLocaleString('en-IN') || '0'}`,
        savings: `${Math.floor((1 - (profileData?.lease?.total_energy_expense || 0) / (profileData?.do_nothing?.bill_amount || 1)) * 100)}%`,
        details: [
          {
            label: 'Fixed charges',
            amount: `₹${profileData?.lease?.fixed?.toLocaleString('en-IN') || '0'}`,
            savings: `${Math.floor((1 - (profileData?.lease?.fixed || 0) / (profileData?.do_nothing?.fixed || 1)) * 100)}%`,
          },
          {
            label: 'Variable charges',
            amount: `₹${profileData?.lease?.variable?.toLocaleString('en-IN') || '0'}`,
            savings: `${Math.floor((1 - (profileData?.lease?.variable || 0) / (profileData?.do_nothing?.variable || 1)) * 100)}%`,
          },
          {
            label: 'Taxes',
            amount: `₹${profileData?.lease?.tax?.toLocaleString('en-IN') || '0'}`,
            savings: `${Math.floor((1 - (profileData?.lease?.tax || 0) / (profileData?.do_nothing?.tax || 1)) * 100)}%`,
          },
          {
            label: 'Solar charges',
            amount: `₹${profileData?.lease?.lease_expense?.toLocaleString('en-IN') || '0'}`,
            savings: 'No%',
          },
          {
            label: 'Electricity expense',
            amount: `₹${
              (
                Number(profileData?.lease?.fixed || 0) +
                Number(profileData?.lease?.variable || 0) +
                Number(profileData?.lease?.lease_expense || 0) +
                Number(profileData?.lease?.tax || 0)
              ).toLocaleString('en-IN') || '0'
            }`,
            savings: 'No%',
          },
          {
            label: 'Solar incentives',
            amount: `- ₹${profileData?.lease?.incentives?.toLocaleString('en-IN') || '0'}`,
            savings: 'No%',
          },
        ],
        totalExpense: `₹${profileData?.lease?.total_energy_expense?.toLocaleString('en-IN') || '0'}`,
        expenseNow: `₹${profileData?.do_nothing?.bill_amount?.toLocaleString('en-IN') || '0'}`,
      },
      {
        title: 'With solar purchase',
        totalCost: `₹${profileData?.own?.bill_amount?.toLocaleString('en-IN') || '0'}`,
        savings: `${Math.floor((1 - (profileData?.own?.bill_amount || 0) / (profileData?.do_nothing?.bill_amount || 1)) * 100)}%`,
        details: [
          {
            label: 'Fixed charges',
            amount: `₹${profileData?.own?.fixed?.toLocaleString('en-IN') || '0'}`,
            savings: `${Math.floor((1 - (profileData?.own?.fixed || 0) / (profileData?.do_nothing?.fixed || 1)) * 100)}%`,
          },
          {
            label: 'Variable charges',
            amount: `₹${profileData?.own?.variable?.toLocaleString('en-IN') || '0'}`,
            savings: `${Math.floor((1 - (profileData?.own?.variable || 0) / (profileData?.do_nothing?.variable || 1)) * 100)}%`,
          },
          {
            label: 'Taxes',
            amount: `₹${profileData?.own?.tax?.toLocaleString('en-IN') || '0'}`,
            savings: `${Math.floor((1 - (profileData?.own?.tax || 0) / (profileData?.do_nothing?.tax || 1)) * 100)}%`,
          },
          {
            label: 'Electricity expense',
            amount: `₹${
              (
                (profileData?.own?.fixed ?? 0) +
                (profileData?.own?.variable ?? 0) +
                (profileData?.own?.tax ?? 0)
              ).toLocaleString('en-IN') || '0'
            }`,
            savings: 'No%',
          },
          {
            label: 'Solar incentives',
            amount: `- ₹${profileData?.own?.incentives?.toLocaleString('en-IN') || '0'}`,
            savings: 'No%',
          },
        ],
        totalExpense: `₹${profileData?.own?.total_energy_expense?.toLocaleString('en-IN') || '0'}`,
        expenseNow: `₹${profileData?.do_nothing?.bill_amount?.toLocaleString('en-IN') || '0'}`,
      },
    ];
  };
  const estimates = getEstimates();

  const ElectricityEstimate = () => {
    return (
      <Box id='details-section'>
        <Typography variant='h4' fontWeight='bold' gutterBottom>
          Your estimated bill
        </Typography>
        <Stack direction='row' spacing={1} mb={3}>
          <Chip
            label='Yearly'
            onClick={() => setSelectedEstimateType('yearly')}
            sx={{
              borderRadius: '9px',
              border: '1px solid #EBEBEB',
              backgroundColor:
                selectedEstimateType === 'yearly'
                  ? 'rgba(0, 0, 0, 0.08)'
                  : 'white',
              '&:hover': {
                backgroundColor:
                  selectedEstimateType === 'yearly'
                    ? 'rgba(0, 0, 0, 0.08)'
                    : 'gray.100',
              },
            }}
          />
          <Chip
            label='High bill month'
            onClick={() => setSelectedEstimateType('high')}
            sx={{
              borderRadius: '9px',
              border: ' 1px solid #EBEBEB',
              backgroundColor:
                selectedEstimateType === 'high'
                  ? 'rgba(0, 0, 0, 0.08)'
                  : 'white',
              '&:hover': {
                backgroundColor:
                  selectedEstimateType === 'high'
                    ? 'rgba(0, 0, 0, 0.08)'
                    : 'gray.100',
              },
            }}
          />
          <Chip
            label='Low bill month'
            onClick={() => setSelectedEstimateType('low')}
            sx={{
              borderRadius: '9px',
              border: ' 1px solid #EBEBEB',
              backgroundColor:
                selectedEstimateType === 'low'
                  ? 'rgba(0, 0, 0, 0.08)'
                  : 'white',
              '&:hover': {
                backgroundColor:
                  selectedEstimateType === 'low'
                    ? 'rgba(0, 0, 0, 0.08)'
                    : 'gray.100',
              },
            }}
          />
        </Stack>

        {estimates.map((estimate, index) => (
          <Box key={index} mb={3} pt={5}>
            <Typography variant='body1' fontWeight='bold'>
              {estimate.title}
            </Typography>
            <Stack
              direction='row'
              width={'45%'}
              justifyContent={'space-between'}
              alignItems='center'
              spacing={1}
              mt={1}
              pb={3.5}
            >
              <Typography variant='h3' fontWeight='bold'>
                {estimate.totalCost}
              </Typography>
              <Chip
                label={`${estimate.savings.startsWith('-') ? '↑' : '↓'}\u00A0\u00A0 ${estimate.savings}`}
                sx={{
                  backgroundColor:
                    estimate.savings === '0%'
                      ? 'rgba(0, 0, 0, 0.08)'
                      : estimate.savings.startsWith('-')
                        ? 'rgb(252,232,230)'
                        : '#dcedc8',
                  color: estimate.savings.startsWith('-') ? 'red' : 'black',
                  width: '80px',
                }}
                size='small'
              />
            </Stack>
            {estimate.details
              .slice(0, estimate.title === 'With postpaid solar' ? 4 : 3)
              .map((item, idx) => (
                <Stack
                  key={idx}
                  direction='row'
                  justifyContent='space-between'
                  mt={1}
                >
                  <Typography variant='body2'>{item.label}</Typography>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <Typography variant='body2' fontWeight='bold'>
                      {item.amount}
                    </Typography>
                    <Chip
                      label={
                        item.savings === 'No%'
                          ? ''
                          : `${item.savings.startsWith('-') ? '↑' : '↓'}\u00A0\u00A0 ${item.savings}`
                      }
                      sx={{
                        backgroundColor:
                          item.savings === 'No%'
                            ? 'white'
                            : item.savings === '0%'
                              ? 'rgba(0, 0, 0, 0.08)'
                              : item.savings.startsWith('-')
                                ? 'rgb(252,232,230)'
                                : '#dcedc8',
                        width: '80px',
                        color: item.savings.startsWith('-') ? 'red' : 'black',
                      }}
                      size='small'
                    />
                  </Stack>
                </Stack>
              ))}

            <Divider sx={{ my: 2 }} />

            {estimate.details
              ?.slice(estimate.title === 'With postpaid solar' ? 4 : 3)
              ?.map((item, idx) => (
                <Stack
                  key={idx}
                  direction='row'
                  justifyContent='space-between'
                  mt={1}
                >
                  <Typography variant='body2'>{item.label}</Typography>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <Typography variant='body2' fontWeight='bold'>
                      {item.amount}
                    </Typography>
                    <Chip
                      label={
                        item.savings === 'No%'
                          ? ''
                          : `${item.savings.startsWith('-') ? '↑' : '↓'}\u00A0\u00A0 ${item.savings}`
                      }
                      sx={{
                        backgroundColor:
                          item.savings === 'No%'
                            ? 'white'
                            : item.savings === '0%'
                              ? 'rgba(0, 0, 0, 0.08)'
                              : item.savings.startsWith('-')
                                ? 'rgb(252,232,230)'
                                : '#dcedc8',
                        width: '80px',
                        color: item.savings.startsWith('-') ? 'red' : 'black',
                      }}
                      size='small'
                    />
                  </Stack>
                </Stack>
              ))}
            <Divider sx={{ my: 2 }} />

            <Stack direction='row' justifyContent='space-between' mt={2}>
              <Typography variant='body2' fontWeight='bold'>
                Net expense
              </Typography>
              <Stack direction='row' spacing={1} alignItems='center'>
                <Typography variant='body2' fontWeight='bold'>
                  {estimate.totalExpense}
                </Typography>
                <Chip
                  label={`${estimate.savings.startsWith('-') ? '↑' : '↓'}\u00A0\u00A0${estimate.savings}`}
                  sx={{
                    backgroundColor:
                      estimate.savings === '0%'
                        ? 'rgba(0, 0, 0, 0.08)'
                        : estimate.savings.startsWith('-')
                          ? 'rgb(252,232,230)'
                          : '#dcedc8',
                    width: '80px',
                    color: estimate.savings.startsWith('-') ? 'red' : 'black',
                  }}
                  size='small'
                />
              </Stack>
            </Stack>

            <Stack direction='row' justifyContent='space-between' mt={1}>
              <Typography variant='body2' fontWeight='bold'>
                Expense now
              </Typography>
              <Stack direction='row' spacing={1} alignItems='center'>
                <Typography variant='body2' fontWeight='bold'>
                  {estimate.expenseNow}
                </Typography>
                <Chip
                  sx={{
                    backgroundColor: 'white',
                    width: '80px',
                  }}
                  size='small'
                />{' '}
              </Stack>
            </Stack>
          </Box>
        ))}
      </Box>
    );
  };

  const getSolarPlantHighlightsData = () => {
    const profileData = roiData?.annual_bill_profile;

    return [
      {
        type: 'POSTPAID',
        expenseReduction: `↓ ${Math.floor(
          (1 -
            (profileData?.lease?.total_energy_expense || 0) /
              (profileData?.do_nothing?.bill_amount || 1)) *
            100,
        )}% expense`,
        savings: `₹${profileData?.lease?.savings?.toLocaleString('en-IN') || '0'}`,
        roi: `${profileData?.lease?.annual_roi || '0'}`,
      },
      {
        type: 'PURCHASE',
        expenseReduction: `↓ ${Math.floor(
          (1 -
            (profileData?.own?.bill_amount || 0) /
              (profileData?.do_nothing?.bill_amount || 1)) *
            100,
        )}% expense`,
        savings: `₹${profileData?.own?.savings?.toLocaleString('en-IN') || '0'}`,
        roi: `${profileData?.own?.annual_roi || '0'}`,
      },
    ];
  };

  const ExpenseComparison = () => {
    const solaPlantHighhlightsData = getSolarPlantHighlightsData();
    return (
      <Box>
        <Stack direction='row' alignItems='center'>
          <Typography variant='h5' fontWeight='bold'>
            {roiData?.annual_bill_profile?.lease.plant_size} solar plan
            highlights
          </Typography>
        </Stack>
        <Box
          display={isMobile ? 'unset' : 'none'}
          sx={{ marginTop: '-30px', pb: '32px' }}
        >
          <img
            style={{ marginTop: '30px' }}
            height={131}
            width={131}
            src={guaranteed}
            alt='guaranteed'
          />
        </Box>
        <Grid container spacing={2} pt={2} pb={6} justifyContent='center'>
          {solaPlantHighhlightsData.map((item, index) => (
            <Grid item xs={6} key={index}>
              <Typography variant='body2'>{item.type}</Typography>
              <Typography variant='caption' color='text.secondary'>
                {item.expenseReduction}
              </Typography>

              <Paper
                sx={{
                  mt: 1,
                  p: 1.5,
                  bgcolor: '#dcedc8',
                  borderRadius: 2,
                  boxShadow: '0',
                }}
              >
                <Typography variant='h6' fontWeight='bold'>
                  {item.savings}
                </Typography>
                <Typography variant='body1' color='text.secondary'>
                  yearly savings
                </Typography>
              </Paper>

              <Paper
                sx={{
                  mt: 1,
                  p: 1.5,
                  bgcolor: '#dcedc8',
                  borderRadius: 2,
                  boxShadow: '0',
                }}
              >
                <Typography variant='h6' fontWeight='bold'>
                  {item.roi}
                </Typography>
                <Typography variant='body1' color='text.secondary'>
                  yearly return
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const PricingTable = () => {
    return (
      <Box padding={'3rem 0'}>
        <Stack
          display='flex'
          flexDirection='row'
          justifyContent='space-between'
          alignItems='center'
          mb={8}
        >
          <Typography variant='body1' component='h1'>
            <Link
              href='#details-section'
              color={'black'}
              sx={{
                textDecoration: 'none',
              }}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('details-section').scrollIntoView({
                  behavior: 'smooth',
                });
              }}
            >
              details below
            </Link>
          </Typography>
          <Box>
            <Button
              variant='contained'
              onClick={handleWhatsAppShare}
              sx={{
                borderRadius: '32px',
                backgroundColor: '#A8D5A8',
                color: '#000',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: '#91c291',
                },
                textTransform: 'none',
                marginRight: '1rem',
              }}
            >
              Share on whatsapp
            </Button>
            <AppointmentRequestPopper roiFormData={formData} />
          </Box>
        </Stack>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ paddingLeft: 0 }}>
                  <Typography>Annual estimates</Typography>
                </TableCell>
                <TableCell>
                  <Typography>Do nothing</Typography>
                </TableCell>
                <TableCell>
                  <Typography>Postpaid solar</Typography>
                </TableCell>
                <TableCell>
                  <Typography>Buy solar</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ paddingLeft: 0, fontWeight: 'bold' }}>
                    <Typography fontWeight={'bold'}>{row.label}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <Typography fontWeight={'bold'}>{row.doNothing}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {row.payLater.split('\n').map((text, i) => (
                      <Typography fontWeight={'bold'} key={i}>
                        {text}
                      </Typography>
                    ))}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <Typography fontWeight={'bold'}>{row.buySolar}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }} mt={5}>
          <AppointmentRequestPopper roiFormData={formData} />
        </Box>
      </Box>
    );
  };

  const handleWhatsAppShare = () => {
    const message = `💰Big savings - I can save ₹${roiData?.annual_bill_profile?.lease?.savings?.toLocaleString('en-IN') || '0'} per year by cutting electricity bills by ${Math.floor((1 - (roiData?.annual_bill_profile?.lease?.total_energy_expense || 0) / (roiData?.annual_bill_profile?.do_nothing?.bill_amount || 1)) * 100)}% and you can too.\n\nThe ROI speaks for itself:\n🌞Buy solar: ${roiData?.annual_bill_profile?.own?.annual_roi || '0'} p. year\n0️⃣ Zero cost solar: ${roiData?.annual_bill_profile?.lease?.annual_roi || '0'} p. year\n🏦 Fixed deposit: 7% p. year\n\nCheck yours here - www.vaysolar.com/en/roi`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <>
      <Box
        component='header'
        sx={isMobile ? mobileHeaderStyle : defaultHeaderStyle}
      >
        <Logo />
      </Box>
      <Box sx={{ maxWidth: 1024, margin: '0 auto', padding: 2 }}>
        <Box display={viewROIForm ? 'unset' : 'none'}>
          <ROIForm
            onSubmit={handleRoiSubmit}
            formDate={handlFromData}
            initialData={formData}
          />
        </Box>
        {!viewROIForm && roiData && (
          <>
            <Popper
              sx={{ zIndex: 1200 }}
              open={open}
              anchorEl={anchorEl}
              placement={placement}
              transition
            >
              {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={350}>
                  <Paper elevation={2}>
                    <Card
                      sx={{
                        maxWidth: 350,
                        padding: 2,
                        borderRadius: 3,
                        boxShadow: 3,
                        position: 'relative',
                      }}
                    >
                      <CardContent sx={{ padding: '16px 16px 0px 16px' }}>
                        <TextField
                          id='plant-size-field'
                          label='Plant size'
                          variant='standard'
                          fullWidth
                          value={customPlantSizeValue}
                          onChange={(e) =>
                            setCustomPlantSizeValue(e.target.value)
                          }
                          helperText='Size of rooftop solar plant'
                        />

                        <TextField
                          mt={6}
                          id='budget-field'
                          label='Budget'
                          variant='standard'
                          fullWidth
                          value={prjectValue}
                          onChange={(e) => setProjectValue(e.target.value)}
                          helperText='Maximum budget of the plant'
                        />
                      </CardContent>
                      <Box
                        display={'flex'}
                        direction={'row'}
                        justifyContent={'flex-end'}
                      >
                        <IconButton onClick={() => setOpen(false)}>
                          <CancelIcon />
                        </IconButton>
                        <IconButton
                          disabled={isSubmitting}
                          onClick={() => {
                            const updatedFormData = {
                              custom_plant_size: customPlantSizeValue,
                              project_value: prjectValue,
                            };
                            setOpen(false);
                            submitUpdatedRoiData(updatedFormData);
                          }}
                        >
                          {isSubmitting ? (
                            <CircularProgress size={24} />
                          ) : (
                            <CheckCircleIcon
                              sx={{ color: theme.palette.secondary.light }}
                            />
                          )}
                        </IconButton>
                      </Box>
                    </Card>
                  </Paper>
                </Fade>
              )}
            </Popper>

            <Popper
              sx={{ zIndex: 1200 }}
              open={bookPopperOpen}
              anchorEl={bookAnchorEl}
              placement='bottom-end'
              transition
            >
              {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={350}>
                  <Paper elevation={2}>
                    <Card
                      sx={{
                        padding: 4,
                        borderRadius: 3,
                        boxShadow: 3,
                        position: 'relative',
                      }}
                    >
                      <CardContent
                        sx={{
                          paddingRight: '140px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography variant='h6' mb={2} fontWeight='bold'>
                          {reportSubmitting ? (
                            <Box display='flex' alignItems='center' gap={2}>
                              <CircularProgress size={20} />
                              <span>Generating report...</span>
                            </Box>
                          ) : (
                            'Get solar savings report'
                          )}
                        </Typography>
                        <TextField
                          required
                          label='Your name'
                          name='name'
                          value={reportFormData.name}
                          onChange={handleAppointmentFormChange}
                          margin='dense'
                          size='small'
                        />
                        <TextField
                          required
                          label='Phone number'
                          name='phone'
                          value={reportFormData.phone}
                          onChange={handleAppointmentFormChange}
                          margin='dense'
                          size='small'
                        />
                        <TextField
                          required
                          label='Pin code'
                          name='pin_code'
                          value={reportFormData.pin_code}
                          onChange={handleAppointmentFormChange}
                          margin='dense'
                          size='small'
                        />
                        <TextField
                          required
                          label='Email address'
                          name='email'
                          value={reportFormData.email}
                          onChange={handleAppointmentFormChange}
                          margin='dense'
                          size='small'
                        />
                      </CardContent>
                      <Box
                        display={'flex'}
                        direction={'row'}
                        justifyContent={'flex-end'}
                        padding={2}
                        pt={0}
                      >
                        <IconButton onClick={handleBookPopperClose}>
                          <CancelIcon fontSize='small' />
                        </IconButton>
                        <Button
                          onClick={() => handleAppointmentSubmit(0)}
                          disabled={reportSubmitting}
                          size='small'
                          variant='caption'
                          fontWeight={'bold'}
                          mt={1.5}
                          startIcon={
                            <CheckCircleIcon
                              sx={{ color: theme.palette.secondary.light }}
                            />
                          }
                        >
                          Download
                        </Button>
                        <Button
                          variant='outlined'
                          onClick={() => handleAppointmentSubmit(1)}
                          sx={{
                            // borderRadius: '32px',
                            // backgroundColor: '#A8D5A8',
                            color: '#000',
                            fontWeight: 'bold',
                            // '&:hover': {
                            //   backgroundColor: '#91c291',
                            // },
                            textTransform: 'none',
                            marginRight: '1rem',
                          }}
                        >
                          Send on whatsapp
                        </Button>
                      </Box>
                      {pdfUrl && (
                        <IconButton
                          onClick={() => window.open(pdfUrl, '_blank')}
                        >
                          <FileDownloadIcon />
                        </IconButton>
                      )}
                    </Card>
                  </Paper>
                </Fade>
              )}
            </Popper>
            <Box pb={6}>
              <Box
                display='flex'
                justifyContent='space-between'
                alignItems='center'
              >
                <Typography variant={isMobile ? 'h5' : 'h5'} fontWeight='bold'>
                  Return on investment
                </Typography>
                <Box>
                  <IconButton
                    sx={{ display: isMobile ? 'none' : 'unset' }}
                    onClick={() => {
                      setViewROIForm(!viewROIForm);
                    }}
                  >
                    <SettingsIcon
                      sx={{ color: theme.palette.secondary.light }}
                    />
                  </IconButton>
                  <IconButton onClick={handleClick('bottom-end')}>
                    <CurrencyRupeeIcon
                      sx={{ color: theme.palette.secondary.light }}
                    />
                  </IconButton>
                </Box>
              </Box>
              <Typography
                variant='body1'
                color={'text.secondary'}
                fontWeight={'bold'}
                gutterBottom
                pb={isMobile ? 0 : 8}
              >
                Flexible plans for all property owners
              </Typography>
              <Box display={isMobile ? 'none' : 'unset'} pt={5}>
                <Typography variant='body2' color='text.secondary'>
                  based on {roiData?.annual_bill_profile?.lease?.load}{' '}
                  {formData?.property_type?.toLowerCase()} load
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  ₹ {formData?.min_bill}-{formData?.max_bill} in monthly bills
                  in {formData?.state} (
                  <Link
                    href='#'
                    underline='hover'
                    onClick={() => {
                      setViewROIForm(!viewROIForm);
                    }}
                  >
                    change
                  </Link>
                  )
                </Typography>
              </Box>

              <Box width={'100%'}>
                <Stack
                  display='flex'
                  flexDirection='row'
                  alignItems={'flex-end'}
                  justifyContent={'space-between'}
                  color={'text.secondary'}
                  width={'100%'}
                  height={isMobile ? 'unset' : '100px'}
                >
                  <Typography
                    variant={isMobile ? 'h5' : 'h1'}
                    fontWeight='600'
                    mt={3}
                    pt={isMobile ? 10 : 10}
                  ></Typography>
                  <Box mt={2} display={isMobile ? 'none' : 'unset'}>
                    <img
                      style={{ marginRight: '-30px', marginBottom: '-128px' }}
                      height={300}
                      width={300}
                      src={guaranteed}
                      alt='guaranteed'
                    />
                  </Box>
                </Stack>
                <Typography
                  sx={{
                    fontFamily: 'Plus Jakarta Sans',
                    fontWeight: 900,
                    letterSpacing: '-0.5px',
                    WebkitTextStroke: isMobile ? '0.6px black' : '2px black',
                    color: '#000',
                  }}
                  variant={isMobile ? 'h4' : 'h1'}
                  fontWeight='600'
                >
                  Save ₹{' '}
                  {roiData?.annual_bill_profile?.lease?.savings?.toLocaleString(
                    'en-IN',
                  ) || '9,000'}{' '}
                  yearly <br /> for next 12 years
                  <Box
                    width={'100%'}
                    display={isMobile ? 'flex' : 'none'}
                    justifyContent={'space-between'}
                  >
                    <Typography variant='body1' component='h1' pt={2}>
                      <Link
                        href='#details-section'
                        color={'black'}
                        sx={{
                          textDecoration: 'none',
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          document
                            .getElementById('details-section')
                            .scrollIntoView({
                              behavior: 'smooth',
                            });
                        }}
                      >
                        details below
                      </Link>
                    </Typography>
                    <img
                      style={{ marginTop: '-28px' }}
                      height={131}
                      width={131}
                      src={guaranteed}
                      alt='guaranteed'
                    />
                  </Box>
                </Typography>
              </Box>
              <Stack
                display={isMobile ? 'flex' : 'none'}
                flexDirection='column'
                gap={1.5}
                color={'black'}
              >
                <AppointmentRequestPopper roiFormData={formData} />
                <Button
                  sx={{
                    borderRadius: '32px',
                    textTransform: 'none',
                  }}
                  variant='outlined'
                  onClick={handleWhatsAppShare}
                >
                  Share on whatsapp
                </Button>
              </Stack>
              <Stack display={isMobile ? 'flex' : 'none'} flexDirection={'row'}>
                <Box pt={5}>
                  <Typography variant='body2' color='text.secondary'>
                    based on {roiData?.annual_bill_profile?.lease?.load}{' '}
                    {formData?.property_type?.toLowerCase()} load
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    ₹ {formData?.min_bill}-{formData?.max_bill} in monthly bills
                    in {formData?.state} (
                    <Link
                      href='#'
                      underline='hover'
                      onClick={() => {
                        setViewROIForm(!viewROIForm);
                      }}
                    >
                      change
                    </Link>
                    )
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => {
                    setViewROIForm(!viewROIForm);
                  }}
                >
                  <SettingsIcon sx={{ color: theme.palette.secondary.light }} />
                </IconButton>
              </Stack>
            </Box>
            {!isMobile && <PricingTable />}
            {isMobile && <ExpenseComparison />}
            {isMobile && <ElectricityEstimate />}
            <Stack
              display={isMobile ? 'flex' : 'none'}
              flexDirection='column'
              gap={1.5}
            >
              <AppointmentRequestPopper roiFormData={formData} />
              <Button
                sx={{
                  borderRadius: '32px',
                  textTransform: 'none',
                }}
                variant='outlined'
                onClick={handleWhatsAppShare}
              >
                Share on whatsapp
              </Button>
            </Stack>
            <Box
              id='details-section'
              borderRadius={'9px'}
              p={2}
              mt={2}
              display={isMobile ? 'none' : 'unset'}
            >
              <Typography variant={isMobile ? 'h5' : 'h4'} pb={2} mb={2} mt={2}>
                How this works?
              </Typography>
              <Stack
                direction='row'
                justifyContent={'space-between'}
                mb={2}
                alignItems={'center'}
              >
                <Typography variant='h3' fontWeight={'bold'} mb={2} mt={2}>
                  Estimated electricity expense
                </Typography>
                {!isMobile && (
                  <Box>
                    <Button
                      variant='contained'
                      onClick={handleWhatsAppShare}
                      sx={{
                        borderRadius: '32px',
                        backgroundColor: '#A8D5A8',
                        color: '#000',
                        fontWeight: 'bold',
                        '&:hover': {
                          backgroundColor: '#91c291',
                        },
                        textTransform: 'none',
                      }}
                    >
                      Share on whatsapp
                    </Button>
                    <Button
                      variant='contained'
                      onClick={handleGetMyReportClick}
                      sx={{
                        marginLeft: 2,
                        borderRadius: '32px',
                        backgroundColor: '#A8D5A8',
                        color: '#000',
                        fontWeight: 'bold',
                        '&:hover': {
                          backgroundColor: '#91c291',
                        },
                        textTransform: 'none',
                      }}
                    >
                      Get my report
                    </Button>
                  </Box>
                )}
              </Stack>
              <Stack
                display='flex'
                flexDirection='row'
                justifyContent='flex-start'
                gap={1}
                mb={2}
                height={40}
                sx={{
                  overflowX: 'auto',
                  whiteSpace: 'nowrap',
                  '&::-webkit-scrollbar': {
                    height: '6px',
                  },
                }}
              >
                {dataToShow.map((data, i) => (
                  <Chip
                    label={data.title}
                    icon={data.icon}
                    key={i}
                    onClick={() => handleChipClick(i)}
                    sx={{
                      borderRadius: '9px',
                      border:
                        selectedChip === i
                          ? '1px solid gray'
                          : '1px solid gray',
                      backgroundColor:
                        selectedChip === i ? 'rgba(0, 0, 0, 0.08)' : 'white',
                      '&:hover': {
                        backgroundColor:
                          selectedChip === i
                            ? 'rgba(0, 0, 0, 0.08)'
                            : 'gray.100',
                      },
                      border: '1px solid #EBEBEB',
                    }}
                  />
                ))}
              </Stack>
              <BillChart
                data={
                  selectedChip === 0
                    ? roiData?.annual_bill_profile
                    : selectedChip === 1
                      ? roiData?.max_bill_profile
                      : selectedChip === 2
                        ? roiData?.mid_bill_profile
                        : roiData?.min_bill_profile
                }
              />
              {selectedChip === 0 && (
                <VisualData
                  data={roiData?.annual_bill_profile}
                  roi_Bylease={roiData?.annual_bill_profile?.lease?.annual_roi}
                  roi_ByOwn={roiData?.annual_bill_profile?.own?.annual_roi}
                />
              )}
              {selectedChip === 1 && (
                <VisualData
                  data={roiData?.max_bill_profile}
                  roi_Bylease={roiData?.max_bill_profile?.lease?.monthly_roi}
                  roi_ByOwn={roiData?.max_bill_profile?.own?.monthly_roi}
                />
              )}
              {selectedChip === 2 && (
                <VisualData
                  data={roiData?.mid_bill_profile}
                  roi_Bylease={roiData?.mid_bill_profile?.lease?.monthly_roi}
                  roi_ByOwn={roiData?.mid_bill_profile?.own?.monthly_roi}
                />
              )}
              {selectedChip === 3 && (
                <VisualData
                  data={roiData?.min_bill_profile}
                  roi_Bylease={roiData?.min_bill_profile?.lease?.monthly_roi}
                  roi_ByOwn={roiData?.min_bill_profile?.own?.monthly_roi}
                />
              )}
            </Box>

            {isMobile && (
              <Box sx={{ margin: '4rem 0' }}>
                <Typography variant='h5' fontWeight='bold' gutterBottom>
                  Your detailed savings report
                </Typography>
                <Button
                  variant='contained'
                  onClick={handleGetMyReportClick}
                  sx={{
                    width: '100%',
                    borderRadius: '32px',
                    backgroundColor: '#A8D5A8',
                    color: '#000',
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: '#91c291',
                    },
                    textTransform: 'none',
                  }}
                >
                  Get my report
                </Button>
              </Box>
            )}
            <FAQSection />
            <PartnerProgram />
          </>
        )}
      </Box>
      <Footer />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant='filled'
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default RoiPage;
